import {
  ColumnDef,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  TableState,
  useReactTable,
} from '@tanstack/react-table';
import React, { FC, ReactElement, useState } from 'react';

import { MessageType } from '@savgroup-front-common/types';

import { Pagination } from '../../atoms/Pagination';
import { ComponentErrorBoundary } from '../../molecules/ErrorBoundary';

import { DEFAULT_PAGE_SIZE, PAGINATION_INTERVALS } from './Datatable.constants';
import { useDatatablePagination } from './Datatable.hooks';
import { $DatatableLayout, $PaginationContainer } from './Datatable.styles';
import {
  DateRubric,
  DefaultRubric,
  Filters,
  RangeNumericRubric,
  Rubric,
} from './Datatable.types';
import { DatatableCore } from './DatatableCore';
import { DatatableFilters } from './DatatableFilters';
import { SearchInputVariant } from './DatatableFilters/DataTableSearchInput/DataTableSearchInput.types';

export enum DatatableModes {
  MANUAL = 'manual',
  CLIENT = 'client',
}

interface DatatableProps<D> {
  hits: D[];
  sorting?: SortingState;
  pageSize?: number;
  pageIndex?: number;
  hitCount?: number;
  columns: ColumnDef<D, any>[];
  initialState?: Partial<TableState>;
  onPaginationChange?: OnChangeFn<PaginationState>;
  onSortingChange?: OnChangeFn<SortingState>;
  isLoading?: boolean;
  SubComponent?: FC<{
    row: Row<D>;
  }>;
  getRowId?: (originalRow: D, index: number, parent?: Row<D>) => string;
  getSubRows?: (originalRow: D, index: number) => undefined | D[];

  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  portalId?: string;

  filters?: Filters;
  selectedFilters?: {
    filtersState?: DefaultRubric[];
    numericFiltersState?: RangeNumericRubric[];
    dateFiltersState?: DateRubric[];
  };
  onSearchQueryChange?: (newValue: string) => void;
  onFilterChange?: (selectedValues: Rubric) => void;
  onFilterClear?: () => void;
  searchInputDefaultValue?: string;
  isSubRowStyled?: boolean;
  isExpandedBackgroundEnabled?: boolean;
  hasPagination?: boolean;
  searchInputPlaceholder?: MessageType | string;
  onClickRow?: (row: any) => void;
  withHeader?: boolean;
  withMinimalWidth?: boolean;
  postSearchInput?: ReactElement;
  componentThemeName?: string;
  dataTestId?: string;
  variant?: SearchInputVariant;
  paginationInterval?: number[];
  enableSubRowSelection?: boolean;
  hasScrollBody?: boolean;
  dataMode?: DatatableModes;
  initialLocalSorting?: SortingState;
  noResultText?: MessageType;
}

export { DEFAULT_PAGE_SIZE, PAGINATION_INTERVALS } from './Datatable.constants';

const Datatable = <D,>({
  sorting,
  pageIndex = 0,
  pageSize = Infinity,
  hits,
  hitCount,
  columns,
  initialState,
  onPaginationChange,
  onSortingChange,
  isLoading = false,
  SubComponent,
  getRowId,
  getSubRows,
  onRowSelectionChange,
  rowSelection = {},
  portalId,
  filters,
  selectedFilters,
  onSearchQueryChange,
  onFilterChange,
  onFilterClear,
  searchInputDefaultValue,
  isSubRowStyled,
  isExpandedBackgroundEnabled = true,
  hasPagination = false,
  searchInputPlaceholder,
  onClickRow,
  withHeader = true,
  withMinimalWidth = false,
  postSearchInput,
  componentThemeName,
  dataTestId,
  paginationInterval = PAGINATION_INTERVALS,
  variant = SearchInputVariant.GLOBAL_SEARCH,
  enableSubRowSelection = true,
  hasScrollBody = false,
  dataMode = DatatableModes.MANUAL,
  initialLocalSorting = [],
  noResultText,
}: DatatableProps<D>): ReactElement => {
  const isClientMode = dataMode === DatatableModes.CLIENT;

  const {
    data,
    pagination,
    handlePaginationChange,
    shouldShowPagination,
    paginationSummary,
  } = useDatatablePagination({
    hits,
    hitCount,
    pageIndex,
    pageSize,
    isClientMode,
    hasPagination,
    onPaginationChange,
    paginationInterval,
  });

  const [expanded, setExpanded] = React.useState<ExpandedState>(
    initialState?.expanded ?? {},
  );

  const [localSorting, setLocalSorting] =
    useState<SortingState>(initialLocalSorting);
  const sortingState: SortingState | undefined = isClientMode
    ? (sorting ?? localSorting)
    : sorting;
  const handleSortingChange: OnChangeFn<SortingState> | undefined = isClientMode
    ? (onSortingChange ?? setLocalSorting)
    : onSortingChange;

  const table = useReactTable({
    data,
    columns,
    initialState,
    pageCount: hitCount ?? -1,
    state: {
      sorting: sortingState,
      pagination,
      expanded,
      rowSelection,
    },
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortingChange,
    onRowSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    ...(isClientMode
      ? {
          getPaginationRowModel: getPaginationRowModel(),
          getSortedRowModel: getSortedRowModel(),
        }
      : {}),
    onExpandedChange: setExpanded,
    getExpandedRowModel: getExpandedRowModel(),
    debugTable: true,
    manualSorting: !isClientMode,
    manualPagination: !isClientMode,
    enableMultiSort: true,
    getRowId,

    getSubRows,
    enableSubRowSelection,
  });

  const isFiltersEnabled = !!(onSearchQueryChange || onFilterChange);

  return (
    <ComponentErrorBoundary>
      <$DatatableLayout
        $hasScrollBody={hasScrollBody}
        data-stretch={hasScrollBody || undefined}
      >
        {isFiltersEnabled && (
          <div>
            <DatatableFilters
              filters={filters}
              onSearchQueryChange={onSearchQueryChange}
              onFilterChange={onFilterChange}
              onFilterClear={onFilterClear}
              selectedFilters={selectedFilters}
              searchInputDefaultValue={searchInputDefaultValue}
              searchInputPlaceholder={searchInputPlaceholder}
              postSearchInput={postSearchInput}
              componentThemeName={componentThemeName}
              dataTestId={dataTestId}
              variant={variant}
            />
          </div>
        )}
        <DatatableCore
          table={table}
          sorting={sortingState}
          isLoading={isLoading}
          pagination={pagination}
          SubComponent={SubComponent}
          portalId={portalId}
          isSubRowStyled={isSubRowStyled}
          isExpandedBackgroundEnabled={isExpandedBackgroundEnabled}
          onClickRow={onClickRow}
          withHeader={withHeader}
          withMinimalWidth={withMinimalWidth}
          noResultText={noResultText}
          dataTestId={dataTestId}
        />
        {shouldShowPagination && (
          <$PaginationContainer>
            <Pagination
              paginationInterval={paginationInterval}
              pagination={paginationSummary}
              onSelectInterval={(newValue) => {
                if (newValue) {
                  table.setPageSize(newValue);
                } else {
                  table.setPageSize(DEFAULT_PAGE_SIZE);
                }
              }}
              onChangePage={(newPage) => {
                table.setPageIndex(newPage - 1);
              }}
              dataTestId={dataTestId}
            />
          </$PaginationContainer>
        )}
      </$DatatableLayout>
    </ComponentErrorBoundary>
  );
};

export default Datatable;
